import {action, computed, observable, runInAction} from 'mobx';
import {EventEmitter} from 'events';
import {getRootStore} from '../../../../app.bootstrap';
import {IWifiAnalyzerResults} from '../../../../services/AngularServices/WifiAnalyzerService';
import isEmpty from 'lodash/isEmpty';
import {WifiScanHistoryEvents} from '../wifi-scan-history-section/WifiScanHistoryController';
import {SCAN_TYPE} from '@techsee/techsee-common/lib/constants/scan.constants';
import {getWifiAnalyzerScanRecordId, normalizeWifiAnalyzerHistoryList} from '../../wifi-scan-history.utils';

export interface IVisualScanHistoryController {
    readonly translate: (key: string, values?: Record<string, any>) => string;
    readonly useNewInterface: boolean;
    readonly dir: string;
    _scanId: string;
    sectionOpen: boolean;
    shouldShow: boolean;
    isEmpty: boolean;
    count: number;
    visualScanHistoryData: IWifiAnalyzerResults[];
    visualScanTranslation: string;
    toggleSection(): void;
    onClickVisualScanHistoryButton(wifiScanId: string): void;
    updateWifiScanHistory(
        refresh: boolean,
        accountId: string,
        userId: string,
        visualScans?: IWifiAnalyzerResults[]
    ): Promise<void>;
    applyVisualScanResults(results: IWifiAnalyzerResults, shouldShow?: boolean): void;
    handleWifiScanResults(data: IWifiAnalyzerResults): void;
}

export class VisualScanHistoryController extends EventEmitter implements IVisualScanHistoryController {
    @observable private _sectionOpen: boolean = false;
    @observable private _visualScanHistoryData: IWifiAnalyzerResults[] = [];
    @observable private _customerWifiScanHistoryLoaded: boolean = false;

    public readonly translate: (key: string, values?: Record<string, any>) => string;
    public readonly useNewInterface: boolean;
    public readonly dir: string;
    public readonly visualScanTranslation: string;

    @observable private _isScreenMirroring: boolean;
    @observable private _isVideoApplicationMode: boolean;
    private readonly _enableVisualScan: boolean;
    private readonly _isWifiScanActive: boolean;
    private readonly _shouldShowWifiScanHistoryInAllModes: boolean;
    private readonly _libraryTaggingMode: boolean;
    public readonly _scanId: string;
    private readonly _wifiAnalyzerService: any;

    constructor(
        useNewInterface: boolean,
        dir: string,
        isScreenMirroring: boolean,
        isVideoApplicationMode: boolean,
        enableVisualScan: boolean,
        isWifiScanActive: boolean,
        shouldShowWifiScanHistoryInAllModes: boolean,
        libraryTaggingMode: boolean,
        accountId: string,
        roomId: string,
        scanId: string
    ) {
        super();
        const rootStore = getRootStore();
        this.translate = rootStore.localizationService.translate;
        this.useNewInterface = useNewInterface;
        this.dir = dir;
        this.visualScanTranslation = this.translate('REACT.WIFI_ANALYZER.VISUAL_SCAN');
        this._isScreenMirroring = isScreenMirroring;
        this._isVideoApplicationMode = isVideoApplicationMode;
        this._enableVisualScan = enableVisualScan;
        this._isWifiScanActive = isWifiScanActive;
        this._shouldShowWifiScanHistoryInAllModes = shouldShowWifiScanHistoryInAllModes;
        this._libraryTaggingMode = libraryTaggingMode;
        this._scanId = scanId;
        this._wifiAnalyzerService = rootStore.wifiAnalyzerService;

        // Fetch initial visual scan data if feature is enabled
        if (this.enableVisualScan && accountId && roomId) {
            this.updateWifiScanHistory(false, accountId, roomId).then();
        }
    }

    @computed
    get sectionOpen(): boolean {
        return this._sectionOpen;
    }

    set sectionOpen(value: boolean) {
        runInAction(() => {
            this._sectionOpen = value;
        });
    }

    @computed
    get visualScanHistoryData(): IWifiAnalyzerResults[] {
        return [...this._visualScanHistoryData];
    }

    set visualScanHistoryData(value: IWifiAnalyzerResults[]) {
        runInAction(() => {
            this._visualScanHistoryData = value || [];
        });
    }

    @computed
    get shouldShow(): boolean {
        const isWifiScanHistoryDisplay =
            (this._isScreenMirroring || this._isVideoApplicationMode) && this._enableVisualScan;

        const isWifiScanActive = this._isWifiScanActive;

        return (
            (isWifiScanHistoryDisplay && isWifiScanActive && !this._libraryTaggingMode) ||
            !!this._scanId ||
            this._shouldShowWifiScanHistoryInAllModes
        );
    }

    @computed
    get isEmpty(): boolean {
        return !this.visualScanHistoryData || this.visualScanHistoryData.length === 0;
    }

    @computed
    get count(): number {
        return this.visualScanHistoryData?.length || 0;
    }

    get enableVisualScan(): boolean {
        return this._enableVisualScan;
    }

    @action
    toggleSection = (): void => {
        this._sectionOpen = !this._sectionOpen;
    };

    @action
    setVisualScanHistoryData = (data: IWifiAnalyzerResults[]): void => {
        this.visualScanHistoryData = normalizeWifiAnalyzerHistoryList(data || []);
    };

    @action
    pushVisualScanData = (data: IWifiAnalyzerResults): void => {
        if (!data) {
            return;
        }
        const combined = [data, ...(this._visualScanHistoryData || [])];
        this._visualScanHistoryData = normalizeWifiAnalyzerHistoryList(combined);
    };

    onClickVisualScanHistoryButton = (visualScanId: string): void => {
        const scan = this.visualScanHistoryData.find((s) => getWifiAnalyzerScanRecordId(s) === visualScanId);
        if (scan) {
            this.applyVisualScanResults(scan, true);
        }
        this.emit(WifiScanHistoryEvents.OPEN_WIFI_SCAN, visualScanId);
    };

    // Update visual scan history - accepts filtered visual scans from main controller
    async updateWifiScanHistory(
        refresh: boolean,
        accountId: string,
        userId: string,
        visualScans?: IWifiAnalyzerResults[]
    ): Promise<void> {
        if (refresh || !this._customerWifiScanHistoryLoaded) {
            runInAction(() => {
                this._customerWifiScanHistoryLoaded = true;
            });

            if (visualScans && visualScans.length > 0) {
                this.setVisualScanHistoryData(visualScans);

                const lastVisualScan = this._visualScanHistoryData[0];
                if (this._enableVisualScan && lastVisualScan) {
                    this.emit(WifiScanHistoryEvents.APPLY_RESULTS, lastVisualScan);
                }
            }

            runInAction(() => {
                this._customerWifiScanHistoryLoaded = false;
            });
        }
    }

    // Apply visual scan results
    applyVisualScanResults = (results: IWifiAnalyzerResults, shouldShow: boolean = false): void => {
        if (!results) {
            return;
        }

        // Emit event for main controller to handle
        this.emit(WifiScanHistoryEvents.APPLY_RESULTS, results, shouldShow);

        if (shouldShow && this._enableVisualScan) {
            this.emit(WifiScanHistoryEvents.UPDATE_SCREEN);
        }
    };

    // Handle new visual scan results from socket
    handleWifiScanResults = (data: IWifiAnalyzerResults): void => {
        if (!isEmpty(data)) {
            // Only handle if it's a visual (AR) scan
            if (data.scanDetails?.scanType === SCAN_TYPE.ar) {
                this.applyVisualScanResults(data, true);
                this.pushVisualScanData(data);
            }
        }
    };

    @action
    setIsScreenMirroring = (isScreenMirroring: boolean): void => {
        this._isScreenMirroring = isScreenMirroring;
    };

    @action
    setIsVideoApplicationMode = (isVideoApplicationMode: boolean): void => {
        this._isVideoApplicationMode = isVideoApplicationMode;
    };

    dispose(): void {
        this.removeAllListeners();
    }
}
